import { Fragment } from "react";

// ui
import {
  Button,
  Typography,
  Modal,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Alert,
  Checkbox,
  TextField,
} from "@mui/material";
import { styled } from "@mui/system";

// jotai
import { useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";

// dnd
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvided,
  DraggableStateSnapshot,
} from "react-beautiful-dnd";

import { CONST } from "../config/const";

const CustomTextField = styled(TextField)({
  "& .MuiInputBase-input": {
    fontSize: "12px",
    height: "12px", // ここでフォントサイズを指定
  },
});
CustomTextField.defaultProps = {
  autoComplete: "off",
};

interface ColumnSettingModalProps {
  onModal: boolean;
  swCloseModal: () => void;
  columnSetting: {
    status: "none" | "open" | "loading" | "result" | "error";
    columns: {
      id: number;
      screen_column_auth: 0 | 1;
      screen_column_system_default: 0 | 1;
      table_disabled: 0 | 1;
      screen_column_serch_disabled: 0 | 1;
      detail_disabled: 0 | 1;
      screen_column_name: string;
      screen_column_type: 0 | 1 | 2;
      table_sort: 0 | 1;
      screen_column_table: 0 | 1;
      table_column_size: string;
      screen_column_sort_id: number;
      screen_column_serch: 0 | 1;
      screen_column_detail: 0 | 1;
    }[];
  };
  setColumnSetting: (status: {
    status: "none" | "open" | "loading" | "result" | "error";
    columns: {
      id: number;
      screen_column_auth: 0 | 1;
      screen_column_system_default: 0 | 1;
      table_disabled: 0 | 1;
      screen_column_serch_disabled: 0 | 1;
      detail_disabled: 0 | 1;
      screen_column_name: string;
      screen_column_type: 0 | 1 | 2;
      table_sort: 0 | 1;
      screen_column_table: 0 | 1;
      table_column_size: string;
      screen_column_sort_id: number;
      screen_column_serch: 0 | 1;
      screen_column_detail: 0 | 1;
    }[];
  }) => void;
  putClumnSetting: () => void;
}

export function ColumnSettingModal(props: ColumnSettingModalProps) {
  const {
    onModal,
    swCloseModal,
    columnSetting,
    setColumnSetting,
    putClumnSetting,
  } = props;
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxHeight: "70vh",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };
  const pathname = useAtomValue(AppAtoms.pathname);

  // ドラッグが完了した際に呼ばれるコールバック
  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    // ドロップ先がない(リスト外にドロップされた)場合は何もしない
    if (!destination) return;
    if (pathname === "/runking") return;

    // 元の場所と同じ場合は並び替え不要
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    // 配列をコピーし、ドラッグされた要素を抜き出し、目的地へ挿入
    const newColumns = Array.from(columnSetting.columns);
    const [removed] = newColumns.splice(source.index, 1);
    newColumns.splice(destination.index, 0, removed);

    // 並び替えが完了した後、sort_id を並び順に合わせて更新する
    // (1スタートの場合は index+1、0スタートなら index を使ってください)
    newColumns.forEach((option: any, index: number) => {
      option.screen_column_sort_id = index + 1;
    });
    // 更新した配列をStateに反映させる
    setColumnSetting({
      ...columnSetting,
      columns: newColumns,
    });
  };

  return (
    <Modal
      open={onModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography id="modal-modal-title" variant="h6" component="h2">
          項目一覧の変更
        </Typography>
        {columnSetting.status === "open" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 5 }}>
            <Box sx={{ marginTop: "5px", maxHeight: "calc(70vh - 80px)" }}>
              <TableContainer
                component={Paper}
                sx={{
                  margin: "1px 1px",
                  width: "calc(100% - 4px)",
                  maxHeight: "calc(70vh - 80px)",
                  border: "1px solid rgb(230, 230, 230)",
                }}
              >
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="droppable">
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{ width: "100%" }}
                      >
                        <Table
                          stickyHeader
                          size="small"
                          aria-label="collapsible table"
                        >
                          <TableHead>
                            <TableRow key="detaiTableRowHead">
                              <TableCell
                                key={"tableColumnKomoku1"}
                                align="center"
                                sx={{
                                  width: "30%",
                                  minWidth: "100px",
                                  backgroundColor:
                                    CONST.tableDetailsBackgroundColor,
                                }}
                              >
                                <Box className="table-header">項目名</Box>
                              </TableCell>
                              <TableCell
                                key={"tableColumnKomoku2"}
                                align="center"
                                sx={{
                                  width: "25%",
                                  minWidth: "70px",
                                  backgroundColor:
                                    CONST.tableDetailsBackgroundColor,
                                }}
                              >
                                <Box className="table-header">項目サイズ</Box>
                              </TableCell>
                              <TableCell
                                key={"tableColumnKomoku3"}
                                align="center"
                                sx={{
                                  width: "15%",
                                  minWidth: "70px",
                                  backgroundColor:
                                    CONST.tableDetailsBackgroundColor,
                                }}
                              >
                                <Box className="table-header">一覧表示</Box>
                              </TableCell>
                              <TableCell
                                key={"tableColumnKomoku4"}
                                align="center"
                                sx={{
                                  width: "15%",
                                  minWidth: "70px",
                                  backgroundColor:
                                    CONST.tableDetailsBackgroundColor,
                                }}
                              >
                                <Box className="table-header">検索表示</Box>
                              </TableCell>
                              <TableCell
                                key={"tableColumnKomoku5"}
                                align="center"
                                sx={{
                                  width: "15%",
                                  minWidth: "70px",
                                  backgroundColor:
                                    CONST.tableDetailsBackgroundColor,
                                }}
                              >
                                <Box className="table-header">詳細表示</Box>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody key={"detaiTableRowBody"}>
                            {columnSetting.columns.map((column, index) => {
                              return (
                                <Draggable
                                  key={column.screen_column_sort_id}
                                  draggableId={String(
                                    column.screen_column_sort_id,
                                  )}
                                  index={index}
                                  isDragDisabled={pathname === "/runking"}
                                >
                                  {(
                                    provided: DraggableProvided,
                                    snapshot: DraggableStateSnapshot,
                                  ) => (
                                    <>
                                      <Fragment
                                        key={"detaiTableRowBody" + index}
                                      >
                                        <TableRow
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          sx={{
                                            backgroundColor: snapshot.isDragging
                                              ? "#f0f8ff"
                                              : "#fff",
                                          }}
                                        >
                                          <TableCell
                                            key={
                                              "detaiTableRowBodyKomoku1_" +
                                              index
                                            }
                                            align="center"
                                          >
                                            <Box className="table-body">
                                              {column.screen_column_name}
                                            </Box>
                                          </TableCell>
                                          <TableCell
                                            key={
                                              "detaiTableRowBodyKomoku2_" +
                                              index
                                            }
                                            align="center"
                                          >
                                            <Box className="table-body">
                                              <CustomTextField
                                                disabled={
                                                  pathname === "/runking"
                                                }
                                                id="textFieldId"
                                                size="small"
                                                value={column.table_column_size}
                                                onChange={async (e) => {
                                                  const inputValue =
                                                    e.target.value;
                                                  if (
                                                    /^\d*$/.test(inputValue)
                                                  ) {
                                                    let _columns =
                                                      columnSetting.columns.concat();
                                                    _columns[index][
                                                      "table_column_size"
                                                    ] = inputValue;
                                                    setColumnSetting({
                                                      ...columnSetting,
                                                      columns: _columns,
                                                    });
                                                  }
                                                }}
                                              />
                                            </Box>
                                          </TableCell>
                                          <TableCell
                                            key={
                                              "detaiTableRowBodyKomoku3_" +
                                              index
                                            }
                                            align="center"
                                          >
                                            <Box className="table-body">
                                              <Checkbox
                                                sx={{
                                                  "& .MuiSvgIcon-root": {
                                                    fontSize: 16,
                                                  },
                                                  marginTop: "-5px",
                                                }}
                                                disabled={
                                                  column.table_disabled === 1
                                                }
                                                checked={
                                                  column.screen_column_table ===
                                                  1
                                                }
                                                onClick={() => {
                                                  let _columns =
                                                    columnSetting.columns.concat();
                                                  if (
                                                    column.screen_column_table ===
                                                    1
                                                  ) {
                                                    _columns[index][
                                                      "screen_column_table"
                                                    ] = 0;
                                                  } else {
                                                    _columns[index][
                                                      "screen_column_table"
                                                    ] = 1;
                                                  }
                                                  setColumnSetting({
                                                    ...columnSetting,
                                                    columns: _columns,
                                                  });
                                                }}
                                              />
                                            </Box>
                                          </TableCell>
                                          <TableCell
                                            key={
                                              "detaiTableRowBodyKomoku4_" +
                                              index
                                            }
                                            align="center"
                                          >
                                            <Box className="table-body">
                                              <Checkbox
                                                sx={{
                                                  "& .MuiSvgIcon-root": {
                                                    fontSize: 16,
                                                  },
                                                  marginTop: "-5px",
                                                }}
                                                disabled={
                                                  column.screen_column_serch_disabled ===
                                                  1
                                                }
                                                checked={
                                                  column.screen_column_serch ===
                                                  1
                                                }
                                                onClick={() => {
                                                  let _columns =
                                                    columnSetting.columns.concat();
                                                  if (
                                                    column.screen_column_serch ===
                                                    1
                                                  ) {
                                                    _columns[index][
                                                      "screen_column_serch"
                                                    ] = 0;
                                                  } else {
                                                    _columns[index][
                                                      "screen_column_serch"
                                                    ] = 1;
                                                  }
                                                  setColumnSetting({
                                                    ...columnSetting,
                                                    columns: _columns,
                                                  });
                                                }}
                                              />
                                            </Box>
                                          </TableCell>
                                          <TableCell
                                            key={
                                              "detaiTableRowBodyKomoku5_" +
                                              index
                                            }
                                            align="center"
                                          >
                                            <Box className="table-body">
                                              <Checkbox
                                                sx={{
                                                  "& .MuiSvgIcon-root": {
                                                    fontSize: 16,
                                                  },
                                                  marginTop: "-5px",
                                                }}
                                                disabled={
                                                  column.detail_disabled === 1
                                                }
                                                checked={
                                                  column.screen_column_detail ===
                                                  1
                                                }
                                                onClick={() => {
                                                  let _columns =
                                                    columnSetting.columns.concat();
                                                  if (
                                                    column.screen_column_detail ===
                                                    1
                                                  ) {
                                                    _columns[index][
                                                      "screen_column_detail"
                                                    ] = 0;
                                                  } else {
                                                    _columns[index][
                                                      "screen_column_detail"
                                                    ] = 1;
                                                  }
                                                  setColumnSetting({
                                                    ...columnSetting,
                                                    columns: _columns,
                                                  });
                                                }}
                                              />
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      </Fragment>
                                    </>
                                  )}
                                </Draggable>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              </TableContainer>
            </Box>
            <Box>
              <Button
                variant="outlined"
                style={{
                  marginTop: "15px",
                  height: "40px",
                  position: "absolute",
                  right: "225px",
                  width: "160px",
                  fontSize: "20px",
                }}
                component="span"
                onClick={() => {
                  putClumnSetting();
                }}
              >
                項目更新
              </Button>
              <Button
                variant="outlined"
                style={{
                  marginTop: "15px",
                  height: "40px",
                  position: "absolute",
                  right: "45px",
                  width: "160px",
                  fontSize: "20px",
                }}
                onClick={() => {
                  swCloseModal();
                }}
              >
                キャンセル
              </Button>
            </Box>
          </Typography>
        ) : columnSetting.status === "error" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 5 }}>
            <Alert severity="error">
              <Box>項目の更新に失敗しました。</Box>
            </Alert>
            <Button
              variant="outlined"
              style={{
                marginTop: "15px",
                height: "40px",
                position: "absolute",
                right: "45px",
                width: "160px",
                fontSize: "20px",
              }}
              onClick={swCloseModal}
            >
              閉じる
            </Button>
          </Typography>
        ) : columnSetting.status === "result" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 5 }}>
            <Box>項目の更新に成功しました。</Box>
            <Button
              variant="outlined"
              style={{
                marginTop: "15px",
                height: "40px",
                position: "absolute",
                right: "45px",
                width: "160px",
                fontSize: "20px",
              }}
              onClick={() => {
                swCloseModal();
              }}
            >
              閉じる
            </Button>
          </Typography>
        ) : null}
      </Box>
    </Modal>
  );
}
